import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import classNames from "classnames";

import Badge from 'components/Badge';
import SVGIcon from 'components/SVGIcon';
import { svgProps } from 'components/icons/SvgContainer';
import AriaLevelDiv from 'components/inputs/AriaLevelDiv';

// StyleGuide = molecules/menus/nav-link

const NavLink = ({
  children,
  className = "",
  disabled,
  active,
  to,
  href,
  target,
  onClick,
  styleVariant = "default",
  notificationQuantity,
  icon,
  iconProps = { width: 12, height: 12 },
  role = "button",
  ariaControls = "",
  ariaExpanded = false,
  ariaHaspopup,
  ariaLevel,
  isFocussable,
  tabIndex,
  state
}) => {
  const finalClassName = classNames(`NavLink nav-item style-${styleVariant}`, className, {
    disabled,
    active,
    'nav-link': (to || href || onClick) && styleVariant !== 'dropdown-item',
    'dropdown-item': styleVariant === 'dropdown-item'
  });

  const dropProps = ariaControls
    ? { 'aria-controls': ariaControls, 'aria-expanded': ariaExpanded }
    : {};

  if (ariaHaspopup) dropProps['aria-haspopup'] = ariaHaspopup;

  const anchorRoleProps = role && role !== 'button' ? { role } : {};

  const finalChildren = (
    <React.Fragment>
      {styleVariant === "settings" && icon && (
        <SVGIcon className="mr-2" name={icon} {...iconProps} />
      )}
      {children}
      {(styleVariant !== "settings" || styleVariant === "storefrontMobile") && icon && (
        <SVGIcon className="ml-2" name={icon} {...iconProps} />
      )}
      {notificationQuantity && (
        <Badge className="ml-2" styleVariant="light">{notificationQuantity}</Badge>
      )}
    </React.Fragment>
  );

  if (to && !disabled) {
    return (
      <Link className={finalClassName} to={to} onClick={onClick} tabIndex={tabIndex} state={state} {...anchorRoleProps}>
        {finalChildren}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a className={finalClassName} href={href} target={target} onClick={onClick} tabIndex={tabIndex} {...anchorRoleProps}>
        {finalChildren}
      </a>
    );
  }

  if (onClick && !disabled) {
    const ariaSelected = role === "tab"
      ? { 'aria-selected': active, 'aria-controls': ariaControls }
      : {};

    return (
      <button
        role={role}
        type="button"
        className={finalClassName}
        onClick={onClick}
        {...ariaSelected}
        {...dropProps}
        tabIndex={tabIndex}
      >
        {finalChildren}
      </button>
    );
  }

  return (
    <AriaLevelDiv
      ariaLevel={ariaLevel != null ? String(ariaLevel) : undefined}
      className={finalClassName}
      isFocussable={isFocussable}
      tabIndex={tabIndex}
    >
      {finalChildren}
    </AriaLevelDiv>
  );
};

NavLink.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  active: PropTypes.bool,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  href: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  notificationQuantity: PropTypes.number,
  icon: PropTypes.string,
  iconProps: PropTypes.shape({ ...svgProps }),
  styleVariant: PropTypes.oneOf([
    "default",
    "settings",
    "QuestionModalTabs",
    "dropdown-item",
    "storefront",
    "storefrontMobile"
  ]),
  role: PropTypes.string,
  ariaControls: PropTypes.string,
  ariaExpanded: PropTypes.bool,
  ariaHaspopup: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  ariaLevel: PropTypes.number,
  isFocussable: PropTypes.bool,
  tabIndex: PropTypes.string,
  state: PropTypes.object
};

export default NavLink;
